import React, { useMemo } from 'react'
import { t } from 'src/translations'
import { BrandedButton } from 'shared/components/common/button'
import { ButtonType, Theme } from 'src/apollo/sites'
import { StyleMetaProps } from 'public/components/default_template/meta/StyleMeta'
import { SiteRestaurantLocation, SiteTheme } from 'shared/js/types'
import classnames from 'classnames'
import {
  generateTextColor,
  Hex
} from 'public/components/default_template/online_ordering/item_tags/tagColorUtils'
import { PhoneIcon } from '@toasttab/buffet-pui-icons'
import { formatPhoneNumber } from 'shared/components/common/form_input/PhoneInput'
import { formatRestaurantAddress } from 'shared/js/locationUtils'
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'
import { getCurrentScheduleInterval, mergeSchedules } from 'src/lib/js/schedule'
import AllHoursModal from 'public/components/default_template/footer/location/AllHoursModal'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

const LocationDetails = ({
  meta,
  siteTheme,
  rxLocation,
  rxName
}: {
  meta?: StyleMetaProps
  siteTheme?: SiteTheme
  rxLocation: SiteRestaurantLocation
  rxName?: string
}) => {
  const tracker = useTracker()
  const darkMode = siteTheme
    ? (siteTheme as Theme).variant?.startsWith('dark')
    : meta?.backgroundColor
    ? generateTextColor(meta.backgroundColor as Hex).toLowerCase() === '#ffffff'
    : false

  const address = rxLocation ? formatRestaurantAddress(rxLocation) : undefined
  const name = rxName ?? rxLocation?.name
  const addressQuery = new URLSearchParams({
    q: `${name ? `${name}, ` : ''}${address}`
  }).toString()

  const mergedSchedules = mergeSchedules(rxLocation, undefined, false)
  const isLocationOpen = useMemo(() => {
    if (mergedSchedules) {
      return !!getCurrentScheduleInterval(
        mergedSchedules,
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )
    }
    return false
  }, [mergedSchedules])

  return (
    <div className={classnames('rxInfoContainer', { darkMode })}>
      <div className='textContent'>
        <div className='rxName'>{name}</div>
        <div className='rxAddress'>{address}</div>
      </div>
      <div className='actions'>
        {rxLocation.phoneNumber && (
          <a
            href={`tel:${formatPhoneNumber(rxLocation.phoneNumber)}`}
            onClick={() =>
              tracker.track(TrackingEvents.PHONE_NUMBER_CLICKED, {
                context: 'error_page',
                restaurant_guid: rxLocation.externalId
              })
            }
          >
            <BrandedButton className='phoneButton' variant={ButtonType.Primary}>
              <PhoneIcon accessibility='decorative' />
              {t('location-details.call', {
                phone: parsePhoneNumber(
                  rxLocation.phoneNumber,
                  (rxLocation.country || 'US') as CountryCode
                )?.formatNational()
              })}
            </BrandedButton>
          </a>
        )}
        <div className='secondaryActions'>
          <a
            href={`https://google.com/maps/place?${addressQuery}`}
            rel='noopener noreferrer'
            target='_blank'
            className='secondaryAction'
            onClick={() =>
              tracker.track(TrackingEvents.GOOGLE_MAPS_LINK_CLICKED, {
                context: 'error_page_link',
                restaurant_guid: rxLocation.externalId
              })
            }
          >
            <BrandedButton variant={ButtonType.Secondary}>
              {t('location-details.directions')}
            </BrandedButton>
          </a>
          <AllHoursModal
            restaurantName={name ?? ''}
            isLocationOpen={isLocationOpen}
            schedule={mergedSchedules}
            timezoneId={Intl.DateTimeFormat().resolvedOptions().timeZone}
          >
            {(open) => {
              return (
                <div className='secondaryAction'>
                  <BrandedButton
                    variant={ButtonType.Secondary}
                    onClick={() => {
                      tracker.track(TrackingEvents.STORE_HOURS_CLICKED, {
                        context: 'error_page',
                        restaurant_guid: rxLocation.externalId
                      })
                      open()
                    }}
                  >
                    {t('location-details.hours')}
                  </BrandedButton>
                </div>
              )
            }}
          </AllHoursModal>
        </div>
      </div>
    </div>
  )
}

export default LocationDetails
